import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { getCable } from "../cable";
import { useAuth } from "./AuthContext";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [internalNotifications, setInternalNotifications] = useState({
    totalUnread: 0,
    byConversation: {},
    byAppointment: 0,
  });
  const [activeConversationId, setActiveConversationId] = useState(null);
  const subscriptionRef = useRef(null);
  const processedNotificationsRef = useRef(new Set());

  const initializeConversationCounts = useCallback((conversations) => {
    setInternalNotifications((prev) => {
      const byConversation = { ...prev.byConversation };

      conversations.forEach((conv) => {
        const convId = String(conv.id);
        if (!byConversation[convId]) {
          byConversation[convId] = conv.unread_count || 0;
        }
      });

      const totalConv = Object.values(byConversation).reduce(
        (sum, c) => sum + c,
        0,
      );

      return {
        ...prev,
        totalUnread: totalConv + prev.byAppointment,
        byConversation,
      };
    });
  }, []);

  const clearConversation = useCallback((conversationId) => {
    const convKey = String(conversationId);

    setInternalNotifications((prev) => {
      const { [convKey]: removed, ...rest } = prev.byConversation;
      const newConversationTotal = Object.values(rest).reduce(
        (sum, count) => sum + count,
        0,
      );
      return {
        ...prev,
        totalUnread: newConversationTotal + prev.byAppointment,
        byConversation: rest,
      };
    });
  }, []);

  const clearAppointments = useCallback(() => {
    setInternalNotifications((prev) => ({
      ...prev,
      totalUnread: Object.values(prev.byConversation).reduce(
        (sum, count) => sum + count,
        0,
      ),
      byAppointment: 0,
    }));
  }, []);

  useEffect(() => {
    if (!token) return;

    if (subscriptionRef.current) {
      try {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      } catch (e) {}
    }

    const cable = getCable(token);
    if (!cable) return;

    const subscription = cable.subscriptions.create(
      { channel: "InternalNotificationsChannel" },
      {
        received: (data) => {
          if (!data.notifiable) return;

          const notificationId =
            data.notification_id ||
            `${data.notifiable.conversation_id}-${data.timestamp}`;

          if (processedNotificationsRef.current.has(notificationId)) return;
          processedNotificationsRef.current.add(notificationId);

          setTimeout(() => {
            processedNotificationsRef.current.delete(notificationId);
          }, 5000);

          if (data.notifiable.conversation_id) {
            const convId = String(data.notifiable.conversation_id);

            if (activeConversationId && String(activeConversationId) === convId)
              return;
            if (
              data.notifiable.sender_id &&
              user &&
              data.notifiable.sender_id === user.id
            )
              return;

            setInternalNotifications((prev) => {
              const current = prev.byConversation[convId] || 0;
              const newByConversation = {
                ...prev.byConversation,
                [convId]: current + 1,
              };
              const totalConv = Object.values(newByConversation).reduce(
                (sum, c) => sum + c,
                0,
              );
              return {
                ...prev,
                totalUnread: totalConv + prev.byAppointment,
                byConversation: newByConversation,
              };
            });

            try {
              window.dispatchEvent(
                new CustomEvent("notification:show-toast", {
                  detail: {
                    message: data.notifiable.message || "New message",
                    conversationId: convId,
                  },
                }),
              );
            } catch (e) {}
          } else if (data.notifiable.appointment_id) {
            setInternalNotifications((prev) => {
              const newByAppointment = prev.byAppointment + 1;
              const totalConv = Object.values(prev.byConversation).reduce(
                (sum, c) => sum + c,
                0,
              );
              return {
                ...prev,
                totalUnread: totalConv + newByAppointment,
                byAppointment: newByAppointment,
              };
            });
          }
        },
      },
    );

    subscriptionRef.current = subscription;

    return () => {
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        } catch (error) {}
      }
    };
  }, [token, activeConversationId, user]);

  return (
    <NotificationContext.Provider
      value={{
        internalNotifications,
        clearConversation,
        clearAppointments,
        initializeConversationCounts,
        activeConversationId,
        setActiveConversation: setActiveConversationId,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useInternalNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useInternalNotification must be used within a NotificationProvider",
    );
  }
  return context;
};
